import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAgentReports, runAgents } from '../services/agent';

export const AIConsultant = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [expandedAgent, setExpandedAgent] = useState(null);

  const fetchReports = async () => {
    if (!id) return;
    try {
      const data = await getAgentReports(id);
      setReports(data);
    } catch (error) {
      console.error("Failed to fetch agent reports:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [id]);

  const handleRunAgents = async () => {
    if (!id) return;
    setRunning(true);
    try {
      await runAgents(id);
      await fetchReports();
    } catch (e) {
      alert("Error running multi-agent pipeline.");
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return <div className="p-8 flex justify-center"><div className="animate-pulse">Loading AI Consultant System...</div></div>;
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Multi-Agent Consulting System</h1>
          <p className="text-gray-500 mt-2">Specialized AI Agents analyzing multiple business dimensions concurrently.</p>
        </div>
        <div className="flex gap-4">
          <button
            onClick={() => navigate(`/analyzer/analysis/${id}/intelligence`)}
            className="px-6 py-2 rounded-lg font-medium text-white bg-indigo-600 hover:bg-indigo-700 transition-colors shadow-sm"
          >
            View Business Intelligence
          </button>
          <button
            onClick={() => navigate(`/analyzer/analysis/${id}/evidence`)}
            className="px-6 py-2 rounded-lg font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            View Scraped Evidence
          </button>
          <button
            onClick={handleRunAgents}
            disabled={running}
            className={`px-6 py-2 rounded-lg font-medium text-white transition-colors ${running ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
          >
            {running ? 'Agents Running...' : 'Rerun Agents'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {reports.map((report) => (
          <div key={report._id} className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div
              className="p-6 cursor-pointer hover:bg-gray-50 transition-colors flex justify-between items-center"
              onClick={() => setExpandedAgent(expandedAgent === report.agent_name ? null : report.agent_name)}
            >
              <div>
                <h3 className="text-xl font-bold text-gray-900">{report.agent_name}</h3>
                <p className="text-sm text-gray-500 mt-1">{report.summary}</p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <div className="text-xs text-gray-400 uppercase font-semibold">Confidence</div>
                  <div className="text-lg font-bold text-blue-600">{(report.confidence * 100).toFixed(0)}%</div>
                </div>
                <svg className={`w-6 h-6 text-gray-400 transform transition-transform ${expandedAgent === report.agent_name ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            {expandedAgent === report.agent_name && (
              <div className="p-6 pt-0 border-t bg-gray-50">
                <div className="mt-4">
                  <h4 className="font-semibold text-gray-800 mb-2">Key Findings:</h4>
                  <ul className="list-disc pl-5 space-y-1 text-gray-600 text-sm">
                    {report.findings.map((finding, idx) => (
                      <li key={idx}>{finding}</li>
                    ))}
                  </ul>
                </div>
                <div className="mt-6">
                  <h4 className="font-semibold text-gray-800 mb-2">Recommendations:</h4>
                  <ul className="list-disc pl-5 space-y-1 text-gray-600 text-sm">
                    {report.recommendations.map((rec, idx) => (
                      <li key={idx}>{rec}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        ))}

        {reports.length === 0 && !running && (
          <div className="col-span-full py-12 text-center text-gray-500 border-2 border-dashed rounded-xl">
            No agent reports found. Run the agents to start analysis.
          </div>
        )}
      </div>
    </div>
  );
};

export default AIConsultant;
